import React, { FC } from 'react';

import { GradientColor } from 'views/OfferDesign/OfferDesign.types';
import { getFontSizeError } from 'views/OfferDesign/OfferDesign.utils';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';

import { Spacing, SPACING_MAP } from '../FormFields.types';
import { getFormFieldValue } from '../FormFields.utils';

import { useFormFieldsContext } from '../../../context';

interface IFontWeightOption {
  value: string;
  label: string;
}

interface IFormFieldsTitleStyleSectionProps {
  heading: string;
  nameField: string;
  nameLabel?: string;
  colorField?: string;
  sizeField: string;
  weightField: string;
  fontWeightOptions: IFontWeightOption[];
  fontWeightButtonDataTestId?: string;
  fontWeightListDataTestId?: string;
  defaults: { fontColor?: GradientColor; fontSize: number };
  sizeRange?: { min: number; max: number };
  spacing?: Spacing;
  showSeparator?: boolean;
  separatorSpacing?: Spacing;
  shouldShowColor?: boolean;
}

export const FormFieldsTitleStyleSection: FC<IFormFieldsTitleStyleSectionProps> = ({
  heading,
  nameField,
  nameLabel,
  colorField,
  sizeField,
  weightField,
  fontWeightOptions,
  fontWeightButtonDataTestId,
  fontWeightListDataTestId,
  defaults,
  sizeRange = { min: 8, max: 20 },
  spacing = 'lg',
  showSeparator = false,
  separatorSpacing = 'md',
  shouldShowColor = true
}): React.ReactElement => {
  const { values, getFieldProps, setFieldValue } = useFormFieldsContext();
  const nameFieldProps = getFieldProps(nameField);
  const sizeFieldProps = getFieldProps(sizeField);
  const fontSizeError = getFontSizeError(Number(getFormFieldValue<number>(values, sizeField) ?? 0), sizeRange.min, sizeRange.max);
  const selectedWeight =
    fontWeightOptions.find((o) => o.value === String(getFormFieldValue<string>(values, weightField) ?? '')) || fontWeightOptions[0];

  return (
    <React.Fragment>
      {showSeparator && <Separator className={`${SPACING_MAP[separatorSpacing]} mb-9 bg-base-border`.trim()} />}
      <h3 className={`text-[20px] font-semibold text-zinc-900 ${SPACING_MAP[spacing]}`.trim()}>
        {heading}
      </h3>

      <div className="mt-4">
        <Input
          label={nameLabel ?? `${heading} Name`}
          name={nameField}
          placeholder={heading}
          value={String(getFormFieldValue<string>(values, nameField) ?? '')}
          onChange={nameFieldProps.onChange}
          onBlur={nameFieldProps.onBlur}
        />
      </div>

      <div className="flex gap-5 mt-4">
        {shouldShowColor && colorField && (
          <div className="flex-1">
            <ColorPicker
              label="Color"
              defaultValue={getFormFieldValue<GradientColor>(values, colorField)?.colorOne || defaults.fontColor?.colorOne}
              onChange={(color) => setFieldValue(colorField, { colorOne: color })}
            />
          </div>
        )}
        <div className="flex-1">
          <Input
            label="Size"
            name={sizeField}
            type="number"
            min={sizeRange.min}
            max={sizeRange.max}
            value={Number(getFormFieldValue<number>(values, sizeField) ?? 0)}
            onChange={sizeFieldProps.onChange}
            onBlur={sizeFieldProps.onBlur}
            state={fontSizeError ? 'error' : 'default'}
            description={fontSizeError}
          />
        </div>
        <div className="flex-1">
          <InputSelectDropdown
            label="Weight"
            options={fontWeightOptions}
            defaultSelected={selectedWeight}
            onOptionChange={(value) => setFieldValue(weightField, value)}
            buttonDataTestId={fontWeightButtonDataTestId}
            listDataTestId={fontWeightListDataTestId}
          />
        </div>
      </div>
    </React.Fragment>
  );
};
